<template>
  <div
    class="m-appointment-card flex flex-col"
    role="button"
    tabindex="0"
    @click.right.prevent="
      () => {
        allowRightClick ? handleAvailableRightClick() : undefined;
      }
    "
  >
    <slot>
      <m-layout-stack no-gap>
        <div class="card-title">
          <div class="appointment-info">
            <span v-if="rotaActivityAssignment.start" class="time"
              >{{ rotaActivityAssignment.start }} –
            </span>
            <span> {{ rotaActivityAssignment.title }}</span>
          </div>
        </div>

        <p class="card-description">
          <span v-if="rotaActivityAssignment?.description"
            >({{ rotaActivityAssignment.description }})
          </span>
          <span> {{ rotaActivityAssignment.formattedDuration }}</span>
        </p>

        <m-layout-stack class="card-footer" gap="1">
          <template v-for="(badge, i) in overlappingBadges" :key="i">
            <m-badge :text="badge" amber />
          </template>
        </m-layout-stack>
      </m-layout-stack>
    </slot>

    <MMenu v-model="isContextMenuShowing" no-auto-open>
      <slot name="context-menu-options"></slot>
    </MMenu>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { randomID } from "../../../../../utils/helpers";
import MLayoutStack from "../../../MLayoutStack/MLayoutStack.vue";
import { RotaActivityAssignment } from "../types";
import MMenu from "../../../MMenu";
import MBadge from "../../../MBadge";

export default defineComponent({
  components: { MBadge, MMenu, MLayoutStack },

  props: {
    rotaActivityAssignment: {
      required: false,
      type: Object as () => RotaActivityAssignment,
      default: null,
    },

    allowRightClick: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const controlId = randomID("m-rota-activity-assignment-card");

    return {
      controlId,
    };
  },

  data() {
    return {
      isContextMenuShowing: false,
    };
  },

  computed: {
    overlappingBadges() {
      if (!this.rotaActivityAssignment.overlappingEntries) {
        return [];
      }
      let v = [];
      for (const a of this.rotaActivityAssignment.overlappingEntries) {
        let b = "Overlapping Appointment";
        if ("rota" === a.type) {
          b = "Overlapping Activity";
        } else if ("break" === a.type) {
          b = "Overlapping Break";
        }
        if (-1 === v.indexOf(b)) {
          v.push(b);
        }
      }
      return v;
    },
  },

  methods: {
    handleAvailableRightClick() {
      this.isContextMenuShowing = !this.isContextMenuShowing;
    },
  },
});
</script>

<style lang="scss">
// BUTTON needs some special handling
button.m-appointment-card {
  // width: calc(100% - 30px);
  width: 100%;
  overflow: hidden;

  text-align: left;
}

.m-appointment-card {
  position: relative;
  padding: 2px 2px 2px var(--gap-2);
  background: #ffffff;
  border-radius: 4px;
  box-shadow: var(--box-shadow);
  flex-wrap: nowrap;
  user-select: none;
  border: 2px solid transparent;

  display: flex;
  flex: 0 0 auto;

  &.selected {
    background: var(--hover-background);
    border-radius: 8px;
  }
  &.ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }
  &.grey {
    opacity: 0.4;
  }
  .card-title {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .appointment-info {
    color: var(--theme-darkest-blue);
    transition: var(--transition);
    font-size: 14px;

    .time {
      font-weight: bold;
    }
  }

  .card-description {
    color: var(--theme-grey);
    word-break: break-word;
    hyphens: auto;
  }

  .card-footer {
    &:empty {
      display: none;
    }

    color: var(--theme-grey);
    margin-top: 0.2em;
    * {
      margin-right: 8px;
    }
  }

  &.with-appointment-type-color {
    &::before {
      content: "";
      position: absolute;
      left: -2px;
      top: -2px;
      bottom: -2px;
      width: 6px;
      background-color: var(--appointment-type-color);
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
    }

    .card-title,
    .card-description,
    .card-footer {
      margin-left: var(--gap-1);
    }
  }
}
.with-hover:hover {
  border-color: var(--theme-blue);

  .appointment-info {
    color: var(--theme-blue);
  }
}
</style>
